export function Skeleton({ className }: { className?: string }) {
  return (
    <div className={`animate-pulse bg-[#1A1F26] rounded ${className}`} />
  );
}

export function MovieCardSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="aspect-[2/3] rounded-lg" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-3 w-1/2" />
    </div>
  );
}

export function CarouselSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-6 w-48" />
      <div className="flex gap-4 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="shrink-0 w-[150px]">
            <MovieCardSkeleton />
          </div>
        ))}
      </div>
    </div>
  );
}

export function MovieDetailSkeleton() {
  return (
    <div className="min-h-screen bg-[#111111] relative">
      {/* Hero Section - matches real: h-[400px] -mt-16 */}
      <div className="relative h-[400px] w-full overflow-hidden -mt-16">
        {/* Navigation buttons area - matches real: absolute top-20 left-4 */}
        <div className="absolute top-20 left-4 z-20 flex gap-2">
          <Skeleton className="h-10 w-20 rounded-lg" />
          <Skeleton className="h-10 w-20 rounded-lg" />
        </div>
        {/* Backdrop skeleton */}
        <Skeleton className="w-full h-full rounded-none" />
        {/* Gradient overlay - matches real */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/60 to-transparent" />
      </div>

      {/* Main Content - matches real: px-6 pb-12 -mt-48 relative z-10 */}
      <div className="px-6 pb-12 -mt-48 relative z-10">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Poster - matches real: flex-shrink-0 > w-[200px] aspect-[2/3] rounded-lg */}
          <div className="flex-shrink-0">
            <Skeleton className="w-[200px] aspect-[2/3] rounded-lg shadow-xl" />
          </div>

          {/* Details - matches real: flex-1 */}
          <div className="flex-1 space-y-4">
            {/* Title + Year */}
            <Skeleton className="h-10 w-2/3" />
            {/* Tagline */}
            <Skeleton className="h-5 w-1/2" />
            {/* Meta row (runtime, genres, ratings) */}
            <div className="flex flex-wrap gap-3">
              <Skeleton className="h-6 w-20" />
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-6 w-16" />
            </div>
            {/* Action buttons */}
            <div className="flex flex-wrap gap-2 pt-2">
              <Skeleton className="h-11 w-36 rounded-lg" />
              <Skeleton className="h-11 w-11 rounded-lg" />
              <Skeleton className="h-11 w-11 rounded-lg" />
              <Skeleton className="h-11 w-11 rounded-lg" />
            </div>
            {/* Overview */}
            <div className="pt-4 space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          </div>
        </div>

        {/* Cast section */}
        <div className="mt-12">
          <Skeleton className="h-7 w-16 mb-4" />
          <div className="flex gap-4 overflow-hidden">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="shrink-0 w-[100px] space-y-2">
                <Skeleton className="w-[100px] h-[100px] rounded-full" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-2/3 mx-auto" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TVDetailSkeleton() {
  // TVDetail uses identical layout wrappers as MovieDetail
  return <MovieDetailSkeleton />;
}

export function PersonDetailSkeleton() {
  return (
    <div className="min-h-screen bg-[#111111] -mt-16 pt-16">
      {/* Back Button - matches real: px-6 py-4 */}
      <div className="px-6 py-4">
        <Skeleton className="h-6 w-20" />
      </div>

      {/* Person info - matches real: px-6 pb-12 */}
      <div className="px-6 pb-12">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Profile image - matches real: flex-shrink-0 > w-[200px] aspect-[2/3] rounded-xl */}
          <div className="flex-shrink-0">
            <Skeleton className="w-[200px] aspect-[2/3] rounded-xl shadow-xl" />
          </div>

          {/* Details - matches real: flex-1 */}
          <div className="flex-1 space-y-4">
            {/* Name */}
            <Skeleton className="h-9 w-64" />
            {/* Role + Age */}
            <Skeleton className="h-5 w-48" />
            {/* Birthplace */}
            <Skeleton className="h-4 w-40" />

            {/* Biography section */}
            <div className="pt-4">
              <Skeleton className="h-6 w-24 mb-3" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            </div>

            {/* Also known as */}
            <div className="pt-4">
              <Skeleton className="h-4 w-28 mb-2" />
              <div className="flex flex-wrap gap-2">
                <Skeleton className="h-7 w-24 rounded-full" />
                <Skeleton className="h-7 w-32 rounded-full" />
                <Skeleton className="h-7 w-20 rounded-full" />
              </div>
            </div>
          </div>
        </div>

        {/* Filmography section */}
        <div className="mt-12">
          <div className="flex items-center gap-4 mb-6">
            <Skeleton className="h-7 w-32" />
            <Skeleton className="h-9 w-24 rounded-lg" />
            <Skeleton className="h-9 w-24 rounded-lg" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="aspect-[2/3] rounded-lg" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
